import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFetch, useIsMobile } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtPct, longDate, todayISO, ORDER_STATUS } from "@/lib/format";
import { Loading, ErrorState } from "@/components/common/States";
import { AllergenList, ProdStatusBadge, AllergenBadge } from "@/components/common/Badges";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

function Kpi({ label, value, sub, tone = "neutral", to, testId }) {
  const tones = { neutral: "text-slate-900", danger: "text-[#A13544]", warning: "text-[#B45309]", ok: "text-[#15803D]" };
  return (
    <Link to={to} className="card flex flex-col gap-1 p-4 transition-shadow hover:shadow-md" data-testid={testId}>
      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</span>
      <span className={`tnum text-2xl font-bold ${tones[tone]}`}>{value}</span>
      {sub && <span className="text-xs text-slate-500">{sub}</span>}
    </Link>
  );
}

function Covers({ value, date, onSaved }) {
  const { can, reason } = useAuth();
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value);
  const save = async () => {
    try { await api.put("/covers", { date, covers: Number(v) }); toast.success("Coperti aggiornati"); setEditing(false); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!editing) return (
    <button className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm disabled:opacity-60" onClick={() => can("covers.write") && setEditing(true)}
      disabled={!can("covers.write")} title={can("covers.write") ? "Modifica coperti previsti" : reason("covers.write")} data-testid="covers-display">
      Coperti previsti: <span className="tnum font-bold">{value}</span><Pencil className="h-3.5 w-3.5 text-slate-400" />
    </button>
  );
  return (
    <div className="flex items-center gap-2">
      <Input type="number" min={0} value={v} onChange={(e) => setV(e.target.value)} className="h-9 w-24 bg-white tnum" data-testid="covers-input" />
      <Button size="sm" onClick={save} data-testid="covers-save">Salva</Button>
      <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Annulla</Button>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const mobile = useIsMobile();
  const { data, loading, error, reload } = useFetch(() => api.get("/dashboard").then((r) => r.data), []);
  if (loading) return <Loading rows={6} />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const k = data.kpi;
  const fcTone = k.food_cost_week == null ? "neutral" : k.food_cost_week > k.food_cost_target ? "danger" : "ok";
  const hour = new Date().getHours();
  const greet = hour < 13 ? "Buongiorno" : hour < 18 ? "Buon pomeriggio" : "Buonasera";

  return (
    <div className="space-y-6 fade-up" data-testid="dashboard-page">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div><h1 className="page-title">{greet}, {user.name.split(" ")[0]}</h1><p className="mt-1 text-sm capitalize text-slate-600">{longDate()}</p></div>
        <Covers value={data.covers} date={todayISO()} onSaved={() => reload(true)} />
      </div>

      {!mobile && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="kpi-grid">
          <Kpi label="Food cost settimana" value={fmtPct(k.food_cost_week)} sub={`Obiettivo ${k.food_cost_target} %`} tone={fcTone} to="/ricette" testId="kpi-food-cost" />
          <Kpi label="Articoli sotto scorta" value={k.under_stock} sub="Vai alle scorte" tone={k.under_stock ? "danger" : "ok"} to="/scorte?stato=sotto_scorta" testId="kpi-under-stock" />
          <Kpi label="Preparazioni di oggi" value={`${k.prep_todo} / ${k.prep_ready}`} sub="da fare / pronte" to="/produzione" testId="kpi-prep" />
          <Kpi label="Ricette da validare" value={k.to_validate} sub="Richiedono l'Executive chef" tone={k.to_validate ? "warning" : "ok"} to="/ricette?stato=da_validare" testId="kpi-to-validate" />
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="card lg:col-span-2" data-testid="production-today">
          <div className="flex items-center justify-between border-b px-4 py-3"><h2 className="text-base font-semibold">Produzione di oggi</h2><Link to="/produzione" className="flex items-center gap-1 text-sm text-primary hover:underline">Piano completo <ArrowRight className="h-3.5 w-3.5" /></Link></div>
          {data.production.length === 0 ? <p className="p-6 text-sm text-slate-500">Nessuna preparazione pianificata per oggi. Aggiungila dal Piano di produzione.</p> : (
            <Table className="dense-table">
              <TableHeader><TableRow><TableHead>Preparazione</TableHead><TableHead className="text-right">Porzioni</TableHead><TableHead className="hidden md:table-cell">Allergeni</TableHead><TableHead>Stato</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.production.map((p) => (
                  <TableRow key={p.id} data-testid={`prod-row-${p.recipe_id}`}>
                    <TableCell><Link to="/produzione" className="font-medium hover:underline">{p.recipe_name}</Link>{p.blocked && <span className="badge badge-red ml-2"><AlertTriangle className="h-3 w-3" />Bloccata</span>}</TableCell>
                    <TableCell className="tnum text-right">{p.portions}</TableCell>
                    <TableCell className="hidden md:table-cell"><AllergenList allergens={p.allergens} max={5} /></TableCell>
                    <TableCell><ProdStatusBadge value={p.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </section>

        <section className="card" data-testid="alerts-panel">
          <div className="border-b px-4 py-3"><h2 className="text-base font-semibold">Avvisi critici</h2></div>
          <ul className="divide-y">
            {data.alerts.length === 0 && <li className="p-6 text-sm text-slate-500">Nessun avviso. Quando compaiono sotto scorta, lotti in scadenza o ricette da rivalidare li trovi qui.</li>}
            {data.alerts.slice(0, 10).map((a) => (
              <li key={`${a.type}-${a.message}`} className="px-4 py-2.5 text-sm">
                <Link to={a.link} className="flex items-start gap-2 hover:underline" data-testid={`alert-${a.type}`}>
                  <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${a.severity === "danger" ? "bg-[#A13544]" : "bg-[#D97706]"}`} /><span>{a.message}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {!mobile && (
        <section className="card" data-testid="deliveries-today">
          <div className="flex items-center justify-between border-b px-4 py-3"><h2 className="text-base font-semibold">Consegne attese oggi</h2><Link to="/ordini" className="flex items-center gap-1 text-sm text-primary hover:underline">Tutti gli ordini <ArrowRight className="h-3.5 w-3.5" /></Link></div>
          {data.deliveries_today.length === 0 ? <p className="p-4 text-sm text-slate-500">Nessuna consegna prevista oggi.{data.late_orders > 0 && <span className="ml-1 text-[#A13544]">{data.late_orders} ordini in ritardo.</span>}</p> : (
            <ul className="divide-y">{data.deliveries_today.map((d) => <li key={d.id} className="flex items-center justify-between px-4 py-2.5 text-sm"><Link to={`/ordini/${d.id}`} className="font-medium hover:underline">{d.number} · {d.supplier_name}</Link><span className="flex items-center gap-3"><span className="text-xs text-slate-500">{d.items_count} righe</span><span className={`badge ${ORDER_STATUS[d.status]?.cls}`}>{ORDER_STATUS[d.status]?.label}</span></span></li>)}
              {data.late_orders > 0 && <li className="px-4 py-2 text-xs text-[#A13544]">{data.late_orders} ordini oltre la data prevista</li>}</ul>
          )}
        </section>
      )}

      {!mobile && (
        <section className="card p-4" data-testid="menu-allergens">
          <div className="flex items-center justify-between"><h2 className="text-base font-semibold">Allergeni del menu di oggi</h2><Link to="/allergeni" className="flex items-center gap-1 text-sm text-primary hover:underline">Matrice completa <ArrowRight className="h-3.5 w-3.5" /></Link></div>
          <div className="mt-3 flex flex-wrap gap-2">
            {Object.keys(data.menu_allergens).length === 0 ? <span className="text-sm text-slate-500">Nessun allergene dichiarato</span>
              : Object.entries(data.menu_allergens).map(([c, s]) => <AllergenBadge key={c} code={c} state={s} showName />)}
          </div>
        </section>
      )}
    </div>
  );
}
